import React, { FC, useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';

import {
  adminActions,
  adminGetCampaignBookings,
  Agencies,
  campaignBookingsSelector,
  isLoadingSelector,
  TimeFormat,
} from '@pharmaplan/common';

import { FormikValues, useFormik } from 'formik';
import DynamicTable from '../../../DynamicTable';
import { IDynamicTableObject } from '../../../DynamicTable/types';
import useTableSort from '../../../../hooks/useTableSort';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import strings from '../../../../localization';

import { campaignBookingsHeader, campaignBookingRow } from './helper';
import useUserProfileLink from '../../../../hooks/Admin/useUserProfileLink';
import useGetItems from '../../../../hooks/useGetItems';
import CampaignBookingHeader from './CampaignBookingHeader';
import EmptyIncompatibleMatches from '../../IncompatibleMatches/EmptyIncompatibleMatches';

const loadingActions = [adminActions.getCampaignBookings];

export enum CampaignBookingType {
  campaign,
  leads,
}
interface ICampaignBookings {
  type: CampaignBookingType;
}

const CampaignBookings: FC<ICampaignBookings> = ({ type }) => {
  const dispatch = useAppDispatch();
  const { state } = useLocation();

  const { campaignId, leadId } = (state ?? {}) as {
    campaignId?: string;
    leadId?: string;
  };

  const { goToUserProfile } = useUserProfileLink();
  const { getImage } = useGetItems();
  const bookings = useAppSelector(campaignBookingsSelector);
  const isLoading = useAppSelector((store) =>
    isLoadingSelector(loadingActions, store));

  const [page, setPage] = useState(1);
  const { isDesc, orderBy, order, handleSorting } = useTableSort();

  const handleSubmit = (values: FormikValues) => {
    setPage(1);
    dispatch(
      adminGetCampaignBookings({
        campaignId,
        page: 1,
        sortBy: orderBy,
        source: values?.source,
        leadId,
        desc: isDesc,
      }),
    );
  };

  const noData = bookings?.data.length === 0;

  let emptyContainerComponent = null;

  if (!isLoading) {
    if (noData) {
      emptyContainerComponent = <EmptyIncompatibleMatches message={strings.noDataAvailable} />;
    }
  }

  const formik = useFormik({
    initialValues: {
      source: Agencies.PharmaPlan,
    },
    onSubmit: handleSubmit,
  });

  const fetchBookings = (targetPage: number) => {
    if (!(campaignId || leadId)) {
      return;
    }

    dispatch(
      adminGetCampaignBookings({
        source: formik.values?.source,
        campaignId,
        leadId,
        page: targetPage,
        sortBy: orderBy,
        desc: isDesc,
      }),
    );
  };

  const table: IDynamicTableObject = {
    headers: campaignBookingsHeader(),
    rows: campaignBookingRow({
      data: bookings?.data,
      goToUserProfile,
      getImage,
      timeFormat: TimeFormat.twentyFourHours,
    }),
    headerBar: [],
  };

  const handlePagination = (_: unknown, selectedPage: number) => {
    const targetPage = selectedPage + 1;
    setPage(targetPage);
    fetchBookings(targetPage);
  };

  const handleReset = () => {
    fetchBookings(1);
    setPage(1);
  };

  useEffect(() => {
    fetchBookings(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, leadId]);

  useEffect(() => {
    if (orderBy) {
      setPage(1);
      fetchBookings(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, order]);

  return (
    <DynamicTable
      table={table}
      showHeader={false}
      loadSuccess={!isLoading}
      emptyContainerComponent={emptyContainerComponent}
      page={page}
      totalCount={bookings?.totalCount}
      order={order}
      orderBy={orderBy}
      handleSort={handleSorting}
      handlePagination={handlePagination}
      customHeader={(
        <CampaignBookingHeader
          backToLabel={
            type === CampaignBookingType.campaign
              ? strings.backToCampaigns
              : strings.backToLeads
          }
          formik={formik}
          handleReset={handleReset}
        />
      )}
    />
  );
};

export default CampaignBookings;
